import {
  HEAT_PUMP_DATASHEET_NOTES,
  HEAT_PUMP_DATASHEET_ROWS,
  HEAT_PUMP_DATASHEET_SOURCES,
} from "./heat-pump-datasheet-data.js";

/**
 * What each outdoor unit on the plan does, as its MANUFACTURER says: EN 12102
 * sound power, installation clearances, and heating capacity at outdoor and
 * flow temperature points. The data itself is generated into
 * `heat-pump-datasheet-data.ts`; this module says what it is and how it may be
 * used.
 *
 * ---------------------------------------------------------------------------
 * WHAT THESE FIGURES ARE — AND ARE NOT
 * ---------------------------------------------------------------------------
 * They are read from each manufacturer's own datasheet, installation manual or
 * data book (URL, revision and page on every figure), on 2026-09-10. They are
 * NOT the MCS Product Directory, which is the reference MIS 3005-D and MCS 020 a)
 * point to — the team has no bulk licence to the Directory (see
 * `heat-pump-catalogue.ts`), and on 2026-09-10 the public Directory listing
 * showed only SCOP per flow temperature for these products, no capacity or
 * sound power. So everything here is offered as a PRE-FILL that a designer
 * accepts or overrides, always labelled
 *
 *     "manufacturer datasheet — confirm in the MCS Product Directory"
 *
 * and is never used silently.
 *
 * ---------------------------------------------------------------------------
 * CAPACITY: WHICH POINTS MAY STAND BEHIND A ≥100% CHECK
 * ---------------------------------------------------------------------------
 * MIS 3005-D §3.4.1(e) wants the heat pump's output at the design flow
 * temperature "without input from any supplementary electric heater", and
 * §3.4.1(f) that it holds the design temperatures "across multiple defrost
 * cycles". So a capacity point is usable only when ALL of these hold
 * (`isUsableForSizing`):
 *
 *   - includesBackupHeater === false (true or not stated: excluded);
 *   - basis is "max" or "nominal" (a figure of unknown basis: excluded);
 *   - defrost is not "excluded" (an instantaneous, defrost-free peak figure
 *     overstates what the unit holds through a cold night: excluded);
 *   - the researcher did not mark it uncertain.
 *
 * "nominal" points are EN 14511 rating points, which for a modulating unit are
 * often part-load — below what it can deliver — so they are conservative for a
 * ≥100% check, and the pre-fill says so. Interpolation (in @sts/calc's
 * `heat-pump-capacity.ts`) is only ever BETWEEN published points of one basis
 * and one defrost treatment, never beyond them.
 */

export type DatasheetSourceKind = "manufacturer" | "secondary";

export interface DatasheetSource {
  readonly url: string;
  readonly document: string;
  readonly revision?: string;
  readonly location?: string;
  readonly accessed: string;
  readonly kind: DatasheetSourceKind;
  /** Anything a reader should know about where the copy was found. */
  readonly note?: string;
}

export type CapacityBasis = "max" | "nominal" | "unknown";
export type DefrostTreatment = "included" | "excluded" | "unknown";

/** [outdoorC, flowC, capacityW, basis, includesBackupHeater, defrost, uncertain, noteIndex, sourceKey] */
export type DatasheetCapacityRow = readonly [
  outdoorC: number,
  flowC: number,
  capacityW: number,
  basis: CapacityBasis,
  includesBackupHeater: boolean | null,
  defrost: DefrostTreatment,
  uncertain: boolean,
  noteIndex: number,
  source: string | null,
];

export interface DatasheetSoundPower {
  readonly dbA: number;
  /** The test condition exactly as the researcher recorded it, e.g. "A7/W55, EN 12102". */
  readonly condition: string;
  readonly uncertain: boolean;
  readonly source: string | null;
}

export interface DatasheetClearances {
  /** mm, or null where the manufacturer prints none for a single unit. */
  readonly back: number | null;
  readonly front: number | null;
  readonly left: number | null;
  readonly right: number | null;
  readonly above: number | null;
  readonly basis: "minimum" | "recommended" | "mixed" | "unknown";
  /** Layout, which side is which, R290 safety zones — read these before relying on the figures. */
  readonly notes: string;
  readonly source: string | null;
}

export interface DatasheetRow {
  /** `OutdoorUnit.id` in plan-objects.ts. */
  readonly id: string;
  readonly modelCodes: string;
  readonly soundPower: DatasheetSoundPower | null;
  readonly soundPowerMax: { readonly dbA: number; readonly condition: string; readonly source: string | null } | null;
  readonly clearances: DatasheetClearances | null;
  readonly wallMountPermitted: boolean | null;
  readonly wallMountNotes: string;
  readonly capacityTableStatus: string;
  readonly capacity: readonly DatasheetCapacityRow[];
  readonly notes: string;
}

/** A capacity point, unpacked, with its note and source resolved. */
export interface DatasheetCapacityPoint {
  readonly outdoorC: number;
  readonly flowC: number;
  readonly capacityW: number;
  readonly basis: CapacityBasis;
  readonly includesBackupHeater: boolean | null;
  readonly defrost: DefrostTreatment;
  readonly uncertain: boolean;
  readonly note: string;
  readonly source: DatasheetSource | undefined;
}

/** How every figure here is to be labelled wherever it is shown or used. */
export const DATASHEET_LABEL = "manufacturer datasheet — confirm in the MCS Product Directory";

export const HEAT_PUMP_DATASHEETS_COMPILED = {
  compiledOn: "2026-09-10",
  method:
    "Read from each manufacturer's own document (URL, revision and page recorded per figure) " +
    "by a research pass on 2026-09-10, converted without change of value.",
} as const;

export function datasheetSource(key: string | null | undefined): DatasheetSource | undefined {
  return key ? HEAT_PUMP_DATASHEET_SOURCES[key] : undefined;
}

export function datasheetFor(catalogueId: string | null | undefined): DatasheetRow | undefined {
  if (!catalogueId) return undefined;
  return HEAT_PUMP_DATASHEET_ROWS.find((row) => row.id === catalogueId);
}

export function datasheetCapacityPoints(row: DatasheetRow): readonly DatasheetCapacityPoint[] {
  return row.capacity.map(
    ([outdoorC, flowC, capacityW, basis, includesBackupHeater, defrost, uncertain, noteIndex, source]) => ({
      outdoorC,
      flowC,
      capacityW,
      basis,
      includesBackupHeater,
      defrost,
      uncertain,
      note: HEAT_PUMP_DATASHEET_NOTES[noteIndex] ?? "",
      source: datasheetSource(source),
    }),
  );
}

/** See the module header: the only points a ≥100% sizing check may rest on. */
export function isUsableForSizing(point: DatasheetCapacityPoint): boolean {
  return (
    point.includesBackupHeater === false &&
    (point.basis === "max" || point.basis === "nominal") &&
    point.defrost !== "excluded" &&
    !point.uncertain
  );
}

/** "Doc title, rev, location (url)" — one line citing a source. */
export function citeDatasheet(source: DatasheetSource | undefined): string {
  if (!source) return "source not recorded";
  const parts = [source.document];
  if (source.revision) parts.push(source.revision);
  if (source.location) parts.push(source.location);
  return `${parts.join(", ")} (${source.url}, read ${source.accessed}${source.kind === "secondary" ? ", secondary source" : ""})`;
}
